// src/pages/Login.tsx
import React, { useState } from "react";
import { auth } from "../firebase";
import {
  signInWithEmailAndPassword,
  getIdTokenResult,
  onAuthStateChanged,
  updatePassword,
} from "firebase/auth";
import { db } from "../firebase";
import { ref, get, child, update } from "firebase/database";
import { useNavigate } from "react-router-dom";

type RoleUI = "student" | "admin";

export default function Login() {
  // --- Visual role toggle (purely cosmetic) ---
  const [roleUI, setRoleUI] = useState<RoleUI>(
    (localStorage.getItem("loginRoleUI") as RoleUI) || "student"
  );
  const roleAccent = roleUI === "student" ? "var(--accent)" : "var(--good)"; // blue vs green
  const subtext =
    roleUI === "student"
      ? "Students are redirected to the Student Dashboard after sign in."
      : "Professors & Admins are redirected to the Faculty Dashboard after sign in.";

  // --- Sign-in form state ---
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // --- Reset password panel state ---
  const [showReset, setShowReset] = useState(false);
  const [rpEmail, setRpEmail] = useState("");
  const [rpCurrent, setRpCurrent] = useState("");
  const [rpNew, setRpNew] = useState("");
  const [rpLoading, setRpLoading] = useState(false);
  const [rpMsg, setRpMsg] = useState<string | null>(null);
  const [rpErr, setRpErr] = useState<string | null>(null);

  // We temporarily pause the auto-redirect when we're in the middle of a reset
  const skipAutoNav = React.useRef(false);

  const navigate = useNavigate();

  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      if (skipAutoNav.current) return; // don't auto-redirect during reset flow
      const { role, college } = await resolveRoleAndCollege(user.uid);
      if (college) sessionStorage.setItem("college", college);
      if (role === "student")
        navigate("/s", { replace: true, state: { college } });
      else navigate("/f", { replace: true, state: { college } });
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setRole(role: RoleUI) {
    setRoleUI(role);
    localStorage.setItem("loginRoleUI", role);
  }

  async function resolveRoleAndCollege(
    uid: string
  ): Promise<{ role: string; college: string }> {
    // 1) Try custom claims first (fast path)
    try {
      const idTokenRes = await getIdTokenResult(auth.currentUser!, true);
      const roleFromClaims = (idTokenRes.claims.role as string) || "";
      const collegeFromClaims = (idTokenRes.claims.college as string) || "";
      if (roleFromClaims && collegeFromClaims) {
        return { role: roleFromClaims, college: collegeFromClaims };
      }
    } catch {
      // ignore and continue to DB lookups
    }

    const root = ref(db);

    // 2) Existing mapping + role (usual path)
    const collegeSnap = await get(child(root, `UserToCollege/${uid}`));
    const roleSnap = await get(child(root, `UserRoles/${uid}`)); // optional
    let college =
      (collegeSnap.exists() && (collegeSnap.val() as string)) ||
      "UNKNOWN_COLLEGE";
    let role = (roleSnap.exists() && (roleSnap.val() as string)) || "student";

    // 3) Self-heal if college is unknown: scan Colleges/*/Users/{uid} once
    if (!college || college === "UNKNOWN_COLLEGE") {
      const collegesSnap = await get(child(root, "Colleges"));
      if (collegesSnap.exists()) {
        const all = collegesSnap.val() as Record<string, any>;
        let foundCollege = "";
        for (const cKey of Object.keys(all)) {
          const users = all[cKey]?.Users;
          if (users && Object.prototype.hasOwnProperty.call(users, uid)) {
            foundCollege = cKey;
            break;
          }
        }
        if (foundCollege) {
          college = foundCollege;
          // write back mapping so future logins are instant
          await update(root, { [`UserToCollege/${uid}`]: college });
        }
      }
    }

    // 4) NEW: Per-college fallback — treat Personal Data/isAdmin=true as admin
    //    This fixes cases where UserRoles/{uid} is missing or stale.
    if (college && college !== "UNKNOWN_COLLEGE") {
      const isAdminSnap = await get(
        child(root, `Colleges/${college}/Users/${uid}/Personal Data/isAdmin`)
      );
      const isAdminVal =
        (isAdminSnap.exists() && Boolean(isAdminSnap.val())) || false;
      if (isAdminVal) {
        role = "admin";
      }
    }

    return { role, college };
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), pw);
      // navigation happens in onAuthStateChanged above
    } catch (e: any) {
      setErr(friendlyAuthError(e, "signin"));
    } finally {
      setLoading(false);
    }
  }

  async function onResetPassword(e: React.FormEvent) {
    e.preventDefault();
    setRpErr(null);
    setRpMsg(null);

    const email = rpEmail.trim();
    const current = rpCurrent;
    const next = rpNew;

    if (!email || !current || !next) {
      setRpErr("Please fill all fields.");
      return;
    }
    if (next.length < 6) {
      setRpErr("New password must be at least 6 characters.");
      return;
    }

    try {
      setRpLoading(true);
      skipAutoNav.current = true;

      // Sign in with current password (re-authenticates securely)
      await signInWithEmailAndPassword(auth, email, current);

      // Update password
      const user = auth.currentUser;
      if (!user) throw new Error("No user");
      await updatePassword(user, next);

      // Sign out so user can sign in with the new password
      await auth.signOut();

      setRpMsg("Password updated. Please sign in with your new password.");
      setRpEmail("");
      setRpCurrent("");
      setRpNew("");
    } catch (e: any) {
      setRpErr(friendlyAuthError(e, "reset"));
    } finally {
      skipAutoNav.current = false;
      setRpLoading(false);
    }
  }

  return (
    <div
      className="container"
      style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}
    >
      {/* Outer card with role accent */}
      <div
        className="card"
        style={{
          width: 420,
          borderColor: roleAccent,
          boxShadow:
            roleUI === "student"
              ? "0 8px 20px rgba(59,130,246,0.25)"
              : "0 8px 20px rgba(16,185,129,0.25)",
        }}
      >
        {/* --- Logo (no clipping now) --- */}
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <img
            src="/VisceR-Login-Logo.png"
            alt="VisceR Logo"
            style={{
              width: 140,
              height: "auto",
              objectFit: "contain",
            }}
          />
        </div>

        {/* Role Toggle */}
        <div
          className="row"
          style={{
            marginBottom: 10,
            alignItems: "stretch",
            gap: 8,
          }}
        >
          <button
            className="btn"
            type="button"
            onClick={() => setRole("student")}
            aria-pressed={roleUI === "student"}
            style={{
              flex: 1,
              borderColor:
                roleUI === "student" ? "var(--accent)" : "var(--line)",
              background:
                roleUI === "student" ? "rgba(59,130,246,0.12)" : "transparent",
            }}
          >
            Student Login
          </button>
          <button
            className="btn"
            type="button"
            onClick={() => setRole("admin")}
            aria-pressed={roleUI === "admin"}
            style={{
              flex: 1,
              borderColor: roleUI === "admin" ? "var(--good)" : "var(--line)",
              background:
                roleUI === "admin" ? "rgba(16,185,129,0.12)" : "transparent",
            }}
          >
            Professor/Admin Login
          </button>
        </div>

        <h2 style={{ marginTop: 4, marginBottom: 8 }}>
          {roleUI === "student"
            ? "Welcome, Student"
            : "Welcome, Professor/Admin"}
        </h2>
        <p
          className="small"
          style={{
            marginTop: -2,
            marginBottom: 16,
            color: roleUI === "student" ? "var(--accent)" : "var(--good)",
          }}
        >
          {subtext}
        </p>

        {/* ---- Sign-in form ---- */}
        <form onSubmit={onSubmit}>
          <label className="small">Email</label>
          <input
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ marginBottom: 10 }}
          />
          <label className="small">Password</label>
          <input
            className="input"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            required
          />
          <button
            className="btn primary"
            type="submit"
            disabled={loading}
            style={{ width: "100%", marginTop: 12 }}
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
          {err && <p style={{ color: "#ff6b6b", marginTop: 12 }}>{err}</p>}
        </form>

        {/* ---- Reset password toggle ---- */}
        <div style={{ marginTop: 16 }}>
          <button
            className="btn"
            type="button"
            onClick={() => {
              setShowReset((s) => !s);
              if (!rpEmail && email) setRpEmail(email); // convenience
            }}
            style={{ width: "100%" }}
          >
            {showReset ? "Hide Reset Password" : "Reset Password"}
          </button>
        </div>

        {/* ---- Reset password panel ---- */}
        {showReset && (
          <div
            className="card"
            style={{
              marginTop: 12,
              borderRadius: 12,
              background: "var(--surface-1)",
              borderColor:
                roleUI === "student" ? "var(--accent)" : "var(--good)",
            }}
          >
            <form onSubmit={onResetPassword}>
              <h3 style={{ marginTop: 0, marginBottom: 8 }}>Reset Password</h3>
              <p className="small" style={{ marginTop: 0, marginBottom: 12 }}>
                Enter your email, current password, and a new password.
              </p>

              <label className="small">Email</label>
              <input
                className="input"
                type="email"
                value={rpEmail}
                onChange={(e) => setRpEmail(e.target.value)}
                required
                style={{ marginBottom: 10 }}
              />

              <label className="small">Current Password</label>
              <input
                className="input"
                type="password"
                value={rpCurrent}
                onChange={(e) => setRpCurrent(e.target.value)}
                required
                style={{ marginBottom: 10 }}
              />

              <label className="small">New Password</label>
              <input
                className="input"
                type="password"
                value={rpNew}
                onChange={(e) => setRpNew(e.target.value)}
                required
              />

              <button
                className="btn primary"
                type="submit"
                disabled={rpLoading}
                style={{ width: "100%", marginTop: 12 }}
              >
                {rpLoading ? "Updating…" : "Update Password"}
              </button>

              {rpErr && (
                <p style={{ color: "#ff6b6b", marginTop: 12 }}>{rpErr}</p>
              )}
              {rpMsg && (
                <p style={{ color: "var(--good)", marginTop: 12 }}>{rpMsg}</p>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- User-friendly error mapping ---------- */
function friendlyAuthError(e: any, context: "signin" | "reset"): string {
  const rawCode = (e?.code || "").toString();
  const code = rawCode.toLowerCase();

  if (code === "auth/invalid-email") {
    return "Please enter a valid email address.";
  }

  if (
    context === "signin" &&
    (code === "auth/wrong-password" ||
      code === "auth/invalid-credential" ||
      code === "auth/invalid-login-credentials")
  ) {
    return "Incorrect email or password.";
  }

  if (
    context === "reset" &&
    (code === "auth/wrong-password" ||
      code === "auth/invalid-credential" ||
      code === "auth/invalid-login-credentials")
  ) {
    return "The current password is incorrect.";
  }

  if (code === "auth/user-not-found") {
    return "No account found for this email.";
  }

  if (code === "auth/user-disabled") {
    return "This account has been disabled. Please contact your administrator.";
  }

  if (code === "auth/weak-password") {
    return "New password is too weak. Please choose a stronger password.";
  }

  if (code === "auth/too-many-requests") {
    return "Too many attempts. Please try again later.";
  }

  if (code === "auth/network-request-failed") {
    return "Network error. Please check your connection and try again.";
  }

  if (code === "auth/operation-not-allowed") {
    return "Email/password sign-in is not enabled for this project.";
  }

  if (code === "auth/requires-recent-login") {
    return "For your security, please sign in again and retry.";
  }

  return context === "signin"
    ? "We couldn't sign you in. Please try again."
    : "We couldn't update your password. Please try again.";
}
